// Pure helpers for ClientTradingInvoiceCreation — the rules are taken from the legacy
// Sl-erp-ui ClientTradingInvoiceCreation.cshtml + its two grid partials.

const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const CLIENT_MASTERDATA = 'ClientMasterdata';

// dd-MMM-yyyy, the format the legacy datepicker posted
export const formatDateForAPI = (val) => {
    if (!val) return '';
    if (typeof val === 'string' && /^\d{2}-[A-Za-z]{3}-\d{4}$/.test(val)) return val;
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) {
        const [yyyy, mm, dd] = val.split('T')[0].split('-');
        return `${dd}-${MONTH_ABBR[parseInt(mm, 10) - 1]}-${yyyy}`;
    }
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return `${String(d.getDate()).padStart(2,'0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

export const round2 = (v) => parseFloat((parseFloat(v) || 0).toFixed(2));

const csv = (list) => list.map(v => `${v},`).join('');

// Company GST option text is "GSTNO,..." — legacy posts only the part before the first comma
export const companyGSTNo = (text) => (text || '').split(',')[0].trim();

// ── Stock picker (NewTradeItemsIssueTempDetails) ──────────────────────────────

// Rows come from several stock tables whose ids can collide, so key by table + id
export const tempKey = (row) => `${row.Tblname}-${row.TempId}`;

// Quantity accepts up to 3 decimals (legacy ValidateAmountQuantity)
export const isValidQtyInput = (value) => value === '' || /^\d*\.?\d{0,3}$/.test(value);

// Selling rate accepts up to 2 decimals (legacy ValidateAmount)
export const isValidRateInput = (value) => value === '' || /^\d*\.?\d{0,2}$/.test(value);

// Checked while typing: a normal stock row can't exceed the row's available qty or the
// store's total qty (ACCQty). ClientMasterdata rows are checked on Add instead, since
// they must equal the full qty and would fail on every keystroke.
export const tempQtyError = (row, value) => {
    if (value === '' || row.Tblname === CLIENT_MASTERDATA) return '';
    const qty = parseFloat(value);
    if (isNaN(qty)) return 'Invalid Quantity';
    const available = parseFloat(row.TempQuantity) || 0;
    const storeQty  = parseFloat(row.ACCQty) || 0;
    return qty > available || qty > storeQty ? 'Invalid Quantity' : '';
};

// Builds the SaveTempTradeNewItemsIssue payload. Only rows with a qty > 0 are sent:
// the SP inserts (and for ClientMasterdata deactivates) every row it is given.
// Returns { error } or { payload }.
export const buildTempIssuePayload = ({ rows, qtys, ccCode, itemCode }) => {
    if (!ccCode)   return { error: 'Select Cost Center' };
    if (!itemCode) return { error: 'Select Item Code' };
    const picked = [];
    for (const row of rows) {
        const qty = parseFloat(qtys[tempKey(row)]) || 0;
        if (qty <= 0) continue;
        if (row.Tblname === CLIENT_MASTERDATA) {
            if (qty !== (parseFloat(row.TempQuantity) || 0)) return { error: 'Invalid Quantity' };
        } else if (tempQtyError(row, String(qty))) {
            return { error: 'Invalid Quantity' };
        }
        picked.push({ row, qty });
    }
    if (!picked.length) return { error: 'Please Add Items to Transfer' };
    return {
        payload: {
            Trowids:         csv(picked.map(p => p.row.TempId)),
            TItemCodes:      csv(picked.map(p => (p.row.TempItemcode || itemCode).trim())),
            TNewQtys:        csv(picked.map(p => p.qty)),
            Ttblnames:       csv(picked.map(p => (p.row.Tblname || '').trim())),
            TCCCode:         ccCode,
            TSingleItemcode: itemCode,
        },
    };
};

// SaveTempTradeItemsIssue answers "Submited,<itemcode>" on success; anything else is the error text
export const isTempAddSuccess = (msg) => typeof msg === 'string' && msg.split(',')[0] === 'Submited';

// ── Item grid (ViewNewTradeIssueItemsGridView) ────────────────────────────────

// One row per added item; qty, HSN and GST % come from the server and are read-only.
// rate is the user's selling rate. Amounts are rounded per row like legacy CountItemTotalAmount.
export const computeItemRow = (gridRow, rate) => {
    const qty    = parseFloat(gridRow.OItemqty) || 0;
    const amount = rate !== '' && rate != null ? round2(qty * (parseFloat(rate) || 0)) : 0;
    const pct = (v) => parseFloat(v) || 0;
    return {
        itemCode: (gridRow.OItemcode || '').trim(),
        qty,
        rate:     rate ?? '',
        amount,
        cgstPer:  pct(gridRow.Ocgstper),
        sgstPer:  pct(gridRow.Osgstper),
        igstPer:  pct(gridRow.OIgstper),
        cgstAmt:  round2(amount * pct(gridRow.Ocgstper) / 100),
        sgstAmt:  round2(amount * pct(gridRow.Osgstper) / 100),
        igstAmt:  round2(amount * pct(gridRow.OIgstper) / 100),
        hsn:      (gridRow.OHSN || '').trim(),
    };
};

export const sumItems = (items) => items.reduce((t, r) => ({
    basic: round2(t.basic + r.amount),
    cgst:  round2(t.cgst + r.cgstAmt),
    sgst:  round2(t.sgst + r.sgstAmt),
    igst:  round2(t.igst + r.igstAmt),
}), { basic: 0, cgst: 0, sgst: 0, igst: 0 });

// GST counts toward the total only once taxes have been added (legacy gststateTradecheck)
export const gstTotal = (taxConfig, totals) => {
    if (!taxConfig) return 0;
    return taxConfig.sameState ? round2(totals.cgst + totals.sgst) : totals.igst;
};

export const invoiceTotal = ({ totals, taxConfig, isTCS, tcsAmount }) =>
    round2(totals.basic + gstTotal(taxConfig, totals) + (isTCS === 'Yes' ? (parseFloat(tcsAmount) || 0) : 0));

// ── Submit ────────────────────────────────────────────────────────────────────

const isPositive = (v) => (parseFloat(v) || 0) > 0;

// Legacy SubmittradeClientInvoiceCreation checks, in the same order. Returns the list of errors.
export const validateInvoice = ({ form, items, checked, taxConfig }) => {
    const errors = [];
    if (!form.CCType)            errors.push('Select Invoice Category');
    if (!form.CCCode)            errors.push('Select Cost Center Code');
    if (!form.Clientcode)        errors.push('Select Client Name');
    if (!form.SubClientcode)     errors.push('Select Sub Client Name');
    if (!form.PONumber)          errors.push('Select Purchase Order Number');
    if (!form.ClientInvoiceNo?.trim()) errors.push('Enter Invoice Number');
    if (!form.RANO?.trim())      errors.push('Enter Running Account No');
    if (!form.InvoiceDate)       errors.push('Select Invoice Date');
    if (!form.InvoiceMakingDate) errors.push('Select Invoice Making Date');
    if (!isPositive(sumItems(items).basic)) errors.push('Invalid Basic Value');
    if (!form.InvoiceRemarks?.trim()) errors.push('Enter Remarks');
    if (!form.IsGstApplicable)   errors.push('Select Is GST Applicable');
    if (form.IsGstApplicable === 'Yes') {
        if (!form.GSTType)       errors.push('Select GST Tax Type');
        if (!form.ClientGSTId)   errors.push('Select Client GST');
        if (!form.CompanyGSTId)  errors.push('Select Company GST');
    }
    if (!form.IsTCSApplicable)   errors.push('Select Is TCS Applicable');
    if (form.IsTCSApplicable === 'Yes') {
        if (!form.TCSDCA)        errors.push('Invalid TCS Accounthead');
        if (!form.TCSSDCA)       errors.push('Invalid TCS Sub-Accounthead');
        if (!isPositive(form.TCSAmount)) errors.push('Invalid TCS Amount');
    }
    if (errors.length) return errors;

    if (form.IsGstApplicable === 'Yes') {
        if (!taxConfig) {
            errors.push('Click on Add Taxes');
        } else {
            const totals = sumItems(items);
            if (!taxConfig.dca) errors.push('Configure Account Head');
            if (taxConfig.sameState) {
                if (!taxConfig.cgstSdca) errors.push('Configure CGST Sub-Account Head');
                if (!taxConfig.sgstSdca) errors.push('Configure SGST Sub-Account Head');
                if (!isPositive(totals.cgst)) errors.push('Enter CGST Amount');
                if (!isPositive(totals.sgst)) errors.push('Enter SGST Amount');
            } else {
                if (!taxConfig.igstSdca) errors.push('Configure Sub-Account Head');
                if (!isPositive(totals.igst)) errors.push('Enter IGST Amount');
            }
        }
    }
    if (errors.length) return errors;

    if (!items.length) errors.push('Invalid Submission');
    if (items.some(r => !checked[r.itemCode])) errors.push('Please Verify ItemCode');
    if (items.some(r => !isPositive(r.amount))) errors.push('Invalid Selling Rate');
    return errors;
};

// SaveTradingClientInvoiceCreation payload, field for field what the legacy MVC controller sent.
export const buildInvoicePayload = ({ form, items, taxConfig, companyGSTText, clientGSTText, userName, roleId }) => {
    const totals = sumItems(items);
    const gst = form.IsGstApplicable === 'Yes' && taxConfig;
    const tcs = form.IsTCSApplicable === 'Yes';
    return {
        PONumber:          form.PONumber,
        RANO:              form.RANO.trim(),
        CCCode:            form.CCCode,
        ClientInvoiceNo:   form.ClientInvoiceNo.trim(),
        InvoiceDate:       formatDateForAPI(form.InvoiceDate),
        InvoiceMakingDate: formatDateForAPI(form.InvoiceMakingDate),
        BasicValue:        totals.basic,
        Total:             invoiceTotal({ totals, taxConfig: gst ? taxConfig : null, isTCS: form.IsTCSApplicable, tcsAmount: form.TCSAmount }),
        InvoiceRemarks:    form.InvoiceRemarks.trim(),
        InvoiceType:       'Trading',
        Clientcode:        form.Clientcode,
        SubClientcode:     form.SubClientcode,
        CreatedBy:         userName,
        Roleid:            roleId,
        // Taxtypes drives the SP's CC choice (Creditable → Capital CC) and TypesOfTaxID
        Taxtypes:          form.IsGstApplicable === 'Yes' ? form.GSTType : '',
        Taxdcas:           gst ? taxConfig.dca : '',
        Taxamounts:        String(gst ? gstTotal(taxConfig, totals) : 0),
        TaxId:             form.IsGstApplicable === 'Yes' ? (parseInt(form.CompanyGSTId, 10) || 0) : 0,
        ClientGST:         form.IsGstApplicable === 'Yes' ? clientGSTText : '',
        CompanyGST:        form.IsGstApplicable === 'Yes' ? companyGSTNo(companyGSTText) : '',
        Statecheck:        gst ? taxConfig.sameState : false,
        Cgstsdca:          gst && taxConfig.sameState ? taxConfig.cgstSdca : '',
        Cgstsdcaamt:       gst && taxConfig.sameState ? totals.cgst : 0,
        Sgstsdca:          gst && taxConfig.sameState ? taxConfig.sgstSdca : '',
        Sgstsdcaamt:       gst && taxConfig.sameState ? totals.sgst : 0,
        Igstsdca:          gst && !taxConfig.sameState ? taxConfig.igstSdca : '',
        Igstsdcaamt:       gst && !taxConfig.sameState ? totals.igst : 0,
        IsGstApplicable:   form.IsGstApplicable,
        IsTCSApplicable:   form.IsTCSApplicable,
        TCSDCA:            tcs ? form.TCSDCA : '',
        TCSSDCA:           tcs ? form.TCSSDCA : '',
        TCSAmount:         tcs ? (parseFloat(form.TCSAmount) || 0) : 0,
        ItemCodes:         csv(items.map(r => r.itemCode)),
        Quantitys:         csv(items.map(r => r.qty)),
        BasicSellings:     csv(items.map(r => parseFloat(r.rate) || 0)),
        SellingAmounts:    csv(items.map(r => r.amount.toFixed(2))),
        cgstper:           csv(items.map(r => r.cgstPer)),
        cgstamt:           csv(items.map(r => r.cgstAmt.toFixed(2))),
        sgstper:           csv(items.map(r => r.sgstPer)),
        sgstamt:           csv(items.map(r => r.sgstAmt.toFixed(2))),
        Igstper:           csv(items.map(r => r.igstPer)),
        Igstamt:           csv(items.map(r => r.igstAmt.toFixed(2))),
        HSN:               csv(items.map(r => r.hsn)),
    };
};
